import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react'
import { apiFetch } from '@/lib/apiFetch'
import { cn } from '@/lib/utils'
import AbcNotation from '@/components/AbcNotation'
import type { SequenceResponse, Warmup } from '@/lib/types'

/** A collapsed-by-default disclosure panel. */
function Disclosure({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="rounded-lg border bg-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[48px] w-full items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="spec-label">{label}</span>
        <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform', open && 'rotate-180')} />
      </button>
      {open && <p className="px-4 pb-4 text-sm text-muted-foreground">{children}</p>}
    </div>
  )
}

function ExerciseView({ warmup }: { warmup: Warmup }) {
  return (
    <div className="space-y-4">
      <h2 className="font-display text-2xl sm:text-3xl font-bold">{warmup.name}</h2>

      <div className="rounded-lg border bg-card px-4 py-6">
        <AbcNotation abc={warmup.abcSource} />
      </div>

      {warmup.description && <Disclosure label="Technique">{warmup.description}</Disclosure>}
      {warmup.purpose && <Disclosure label="Why this works">{warmup.purpose}</Disclosure>}
    </div>
  )
}

export default function ToastPage() {
  const location = useLocation()
  const [sequence, setSequence] = useState<SequenceResponse | null>(null)
  const [error, setError] = useState('')
  const [index, setIndex] = useState(0)
  const [done, setDone] = useState(false)

  const query = useMemo(() => new URLSearchParams(location.search), [location.search])

  useEffect(() => {
    const focuses = (query.get('focuses') ?? '').split(',').filter(Boolean)
    const size = Number(query.get('size')) || undefined
    const limitToMine = query.get('limitToMine') === 'true'
    apiFetch<SequenceResponse>('/sequences/build', {
      method: 'POST',
      body: JSON.stringify({ size, focuses, limitToMine }),
    })
      .then((seq) => {
        setSequence(seq)
        setIndex(0)
        setDone(false)
      })
      .catch((e) => setError(e.message))
  }, [query])

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <p className="text-destructive">{error}</p>
        <Link to="/" className="mt-4 inline-block underline">Back</Link>
      </div>
    )
  }

  if (!sequence) {
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center">
        <p className="spec-label">Building your warm-up…</p>
      </div>
    )
  }

  if (sequence.warmups.length === 0) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <h1 className="font-display text-3xl font-bold">Nothing matched.</h1>
        <p className="mt-3 text-muted-foreground">
          No exercises fit that selection yet. The library may need a few more.
        </p>
        <Link to="/" className="mt-6 inline-block underline">Adjust the selection</Link>
      </div>
    )
  }

  if (done) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        <p className="spec-label mb-4">Warm-up complete</p>
        <h1 className="font-display text-5xl font-bold">Golden brown.</h1>
        <p className="mt-4 text-muted-foreground">You're warmed up. Go sing something.</p>
        <div className="mt-8 flex justify-center gap-4">
          <button onClick={() => { setIndex(0); setDone(false) }} className="rounded-full border px-6 py-3 font-medium min-h-[48px]">
            Run it again
          </button>
          <Link to="/" className="rounded-full bg-primary px-6 py-3 font-medium text-primary-foreground min-h-[48px] inline-flex items-center">
            New warm-up
          </Link>
        </div>
      </div>
    )
  }

  const current = sequence.warmups[index]
  const isLast = index === sequence.warmups.length - 1

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {sequence.unmatchedFocuses.length > 0 && (
        <p className="mb-4 rounded-md border border-golden bg-secondary px-3 py-2 text-sm">
          No exercises matched: {sequence.unmatchedFocuses.join(', ')}. The rest of the warm-up
          proceeds as planned.
        </p>
      )}

      {sequence.missingVoices.length > 0 && (
        <p className="mb-4 rounded-md border border-golden bg-secondary px-3 py-2 text-sm">
          Couldn't fill: {sequence.missingVoices.join(', ')} (no matching exercise).
        </p>
      )}

      <div className="mb-6 flex items-center justify-between">
        <p className="spec-label">Exercise {index + 1} of {sequence.warmups.length}</p>
        <div className="flex gap-1.5">
          {sequence.warmups.map((w, i) => (
            <span
              key={w.id}
              className={cn(
                'h-2 w-6 rounded-full transition-colors',
                i < index ? 'bg-golden' : i === index ? 'bg-coil' : 'bg-muted'
              )}
            />
          ))}
        </div>
      </div>

      <ExerciseView key={current.id} warmup={current} />

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className={cn(
            'inline-flex min-h-[48px] items-center gap-1 rounded-full border px-5 font-medium',
            index === 0 && 'opacity-40'
          )}
        >
          <ChevronLeft className="h-4 w-4" /> Previous
        </button>
        <button
          onClick={() => (isLast ? setDone(true) : setIndex((i) => i + 1))}
          className="inline-flex min-h-[48px] items-center gap-1 rounded-full bg-primary px-6 font-medium text-primary-foreground"
        >
          {isLast ? 'Finish' : 'Next'} {!isLast && <ChevronRight className="h-4 w-4" />}
        </button>
      </div>
    </div>
  )
}
